import { defineStore } from 'pinia'
import axios from 'axios'

import { uuidv4 } from '@/api/uuid'

import {
  calculatePrice,
  calculateMailingPrice
} from '../features/productOrder/ProductOrderApi'

import Cookies from 'js-cookie'
import { UPLOAD_COOKIE, useRootStore } from '@/stores/store'
import { findIndex, isArray, isEmpty } from 'lodash-es'
import ProductOrderMixin from '../features/productOrder/ProductOrderMixin'

export const UPLOAD_SESSION_EXPIRY_DAYS = 7

export const useFileListStore = defineStore('fileList', {
  state: () => ({
    fileList: [],
    archives: [],
    priceRequestCounters: {}, // Track price request counters per fileName
    isUploading: false
  }),
  getters: {
    selectedFileIds(state) {
      return state.fileList.map((file) => file.fileId)
    },
    getFile: (state) => {
      return (id) => state.fileList.find((file) => file.fileId === id)
    },
    containsFile: (state) => {
      return (filename) =>
        state.fileList.find((file) => file.fileName === filename) !== undefined
    },
    getAllFiles(state) {
      return state.fileList
    }
  },
  actions: {
    updatePrintSpecs({ update }) {
      // Update can be a single item
      if (!isArray(update)) {
        update = [update]
      }

      for (let file of update) {
        let prodSpecs
        if (
          !useRootStore().theme.customFields.printOrder?.cartV2 &&
          file.productSpecs?.userTemplate
        ) {
          prodSpecs = {}
        } else {
          prodSpecs = file.productSpecs || {}
        }

        const newSpecs = {
          ...file,
          productSpecs: prodSpecs
        }

        const index = findIndex(this.fileList, { uuid: file.uuid })

        if (index !== -1) {
          // Update in-place in the array
          this.fileList.splice(index, 1, newSpecs)
        } else {
          this.fileList.push(newSpecs)
        }
      }
    },
    setFileList({ files, clean }) {
      if (clean) {
        this.fileList = []
      }

      let newFiles = []

      files.forEach((file) => {
        if (!file.template) {
          file.template = ''
        }

        if (!file.productSpecs || isEmpty(file.productSpecs)) {
          file.productSpecs = {}
        } else if (!file.productSpecs.title) {
          file.productSpecs.title = file.productSpecs.Product
          delete file.productSpecs.Product
        }

        // For old cart (cartV2=false): map productSpecs fields back to root-level
        // The backend stores specs in productSpecs, but the old cart reads from root
        if (!useRootStore().theme.customFields.printOrder?.cartV2) {
          const specs = file.productSpecs
          if (specs.format) file.format = specs.format
          if (specs.colorMode) file.colorMode = specs.colorMode
          if (specs.side) file.side = specs.side
          if (specs.material) file.material = specs.material
          if (specs.treatment) file.treatment = specs.treatment
          if (specs.paperType) file.paperType = specs.paperType
        }

        if (
          !this.fileList.find((flFile) => {
            return flFile.fileName === file.fileName
          })
        ) {
          newFiles.push(file)
        }
      })
      this.fileList = [...this.fileList, ...newFiles]
    },
    updateFileSpec(files, spec, value) {
      // function to update specs of files in the fileList
      // val = [[files],"spec to update","value of spec"]
      this.fileList.forEach((file) => {
        let val

        if (files.indexOf(file.fileName) > -1) {
          if (spec === 'quantity') {
            val = parseInt(value)
          } else {
            val = value
          }

          if (spec === 'inCart') {
            file[spec] = value
          } else {
            file[spec] = val
          }
        }
      })
    },
    updateProductSpec(files, spec, value) {
      // function to update productspecs of files in the fileList
      // value = [[files],"spec to update","value of spec"]
      this.fileList.forEach((file) => {
        if (files.indexOf(file.fileName) > -1) {
          if (value === '') {
            delete file.productSpecs[spec]
            //del spec
          } else {
            const specObj = {}
            specObj[spec] = value
            file.productSpecs = Object.assign({}, file.productSpecs, specObj)
          }
        }
      })
    },
    addProduct(files, product) {
      // function to add product to files in the fileList
      // value = [[files],{product}]
      this.fileList.forEach((file) => {
        if (files.indexOf(file.fileName) > -1) {
          file.productSpecs = Object.assign({}, product)
        }
      })
    },
    addProductOrderProduct(value) {
      this.fileList.forEach((file) => {
        if (value[0].indexOf(file.fileName) > -1) {
          file.price = 0
          file.productSpecs = Object.assign({}, value[1])
          file.divisionCode = value[2] ?? null
        }
      })
    },
    setProductOrderSpec(value) {
      this.fileList.forEach((file) => {
        if (value[0].indexOf(file.fileName) > -1) {
          file.productSpecs.specs = [...value[1]]
        }
      })
    },
    updateProductOrderQuantity(value) {
      this.fileList.forEach((file) => {
        if (value[0].indexOf(file.fileName) > -1) {
          //calculate
          file.quantity = value[1]
        }
      })
    },
    updateProductOrderPrice(value) {
      this.fileList.forEach((file) => {
        if (file.fileName === value[0]) {
          file.price = value[1].pricePerItem
        }
      })
    },
    deleteFile(fileId) {
      this.fileList = this.fileList.filter((file) => file.uuid !== fileId)
    },
    clear() {
      this.fileList = []
    },
    async getFiles(clean = false, rootIsLoggedIn = false) {
      // rootIsLoggedIn moet als param worden meegegeven bij gebruik in Pinia (voorheen rootGetters)
      const sessionId = Cookies.get(UPLOAD_COOKIE)
      const url = `/api/v3/upload/${sessionId}/print-specs`

      try {
        const response = await axios.get(url)
        this.setFileList({
          files: response.data.printSpecs,
          clean: clean
        })
        if (rootIsLoggedIn) {
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.clear()
        }
        // 404 is geen probleem
      }
    },
    removeAllFiles() {
      this.clear()
    },
    async removeFile(fileId) {
      const sessionId = Cookies.get(UPLOAD_COOKIE)

      // Find the file to remove
      const file = this.fileList.find((f) => f.uuid === fileId)

      // Check if file has references to other files (ADDRESS_LIST, ATTACHMENT_FILE, etc.)
      if (file?.productSpecs?.files && file.productSpecs.files.length > 0) {
        for (const referencedFile of file.productSpecs.files) {
          // Find the UUID of the referenced file in the fileList
          const fileToRemove = this.fileList.find(
            (f) =>
              f.uploadFileType === referencedFile.type &&
              f.fileName === referencedFile.name
          )
          if (fileToRemove) {
            await axios.delete(
              `/api/v3/upload/${sessionId}/${fileToRemove.uuid}`
            )
            this.deleteFile(fileToRemove.uuid)
          }
        }
      }

      await axios.delete(`/api/v3/upload/${sessionId}/${fileId}`)
      this.deleteFile(fileId)
    },
    async removeFiles(fileIds) {
      const sessionId = Cookies.get(UPLOAD_COOKIE)
      const allIdsToRemove = [...fileIds]

      // Collect all referenced files from all files to be removed
      for (const fileId of fileIds) {
        const file = this.fileList.find((f) => f.uuid === fileId)

        // Check if file has references to other files (ADDRESS_LIST, ATTACHMENT_FILE, etc.)
        if (file?.productSpecs?.files && file.productSpecs.files.length > 0) {
          for (const referencedFile of file.productSpecs.files) {
            // Find the UUID of the referenced file in the fileList
            const fileToRemove = this.fileList.find(
              (f) =>
                f.uploadFileType === referencedFile.type &&
                f.fileName === referencedFile.name
            )
            if (fileToRemove && !allIdsToRemove.includes(fileToRemove.uuid)) {
              allIdsToRemove.push(fileToRemove.uuid)
            }
          }
        }
      }

      await axios.post(`/api/v3/upload/delete-files`, {
        sessionId: sessionId,
        fileIds: allIdsToRemove
      })
      allIdsToRemove.forEach((fileId) => {
        this.deleteFile(fileId)
      })
    },
    async setProductOrderPrice(options) {
      this.fileList.forEach((file) => {
        if (
          options.fileNames.indexOf(file.fileName) > -1 &&
          // User templates hebben specs als object: geen prijsberekening mogelijk
          Array.isArray(file.productSpecs?.specs)
        ) {
          // Initialize counter for this file if it doesn't exist
          if (!this.priceRequestCounters[file.fileName]) {
            this.priceRequestCounters[file.fileName] = 0
          }

          // Increment counter and store current request ID
          this.priceRequestCounters[file.fileName]++
          const currentRequestId = this.priceRequestCounters[file.fileName]

          //setup correct priceobject and get the price
          let priceObject = {}
          if (file.productSpecs.priceType === 'mailing') {
            // For mailing orders, structure is different:
            // letterFile + optional attachmentFile + quantity + options
            // Check if there's an address list file
            const hasAddressList = file.productSpecs.files?.some(
              (f) => f.type === 'ADDRESS_LIST'
            )

            priceObject = {
              letterFile: {
                aantalPaginasKleur: file.productSpecs.alwaysColor
                  ? file.pagesColour + file.pagesBW
                  : file.pagesColour,
                aantalPaginasZwartWit: file.productSpecs.alwaysColor
                  ? 0
                  : file.pagesBW,
                aantalPaginasBlanco: file.pagesBlank,
                formaat: '',
                achterzijde: {},
                bindwijze: '',
                afwerking: [],
                dubbelzijdig: false,
                orderAfwerking: [],
                papierType: '',
                printKleur: '',
                voorzijde: {}
              },
              quantity: file.quantity,
              mergeAddresses: hasAddressList, // true if ADDRESS_LIST exists
              envelopeEnclosing: false
            }

            // Check if there's an attachment file
            if (file.productSpecs.files?.length > 0) {
              const attachmentFile = file.productSpecs.files.find(
                (f) => f.type === 'ATTACHMENT_FILE'
              )
              if (attachmentFile) {
                // Find the actual file in fileList to get its specs
                const attachmentFileData = this.fileList.find(
                  (f) =>
                    f.fileName === attachmentFile.name &&
                    f.uploadFileType === 'ATTACHMENT_FILE'
                )
                if (attachmentFileData) {
                  priceObject.attachmentFile = {
                    aantalPaginasKleur: attachmentFileData.productSpecs
                      ?.alwaysColor
                      ? attachmentFileData.pagesColour +
                        attachmentFileData.pagesBW
                      : attachmentFileData.pagesColour,
                    aantalPaginasZwartWit: attachmentFileData.productSpecs
                      ?.alwaysColor
                      ? 0
                      : attachmentFileData.pagesBW,
                    aantalPaginasBlanco: attachmentFileData.pagesBlank,
                    formaat: '',
                    achterzijde: {},
                    bindwijze: '',
                    afwerking: [],
                    dubbelzijdig: false,
                    orderAfwerking: [],
                    papierType: '',
                    printKleur: '',
                    voorzijde: {}
                  }
                }
              }
            }
          } else {
            priceObject = {
              aantalPaginasKleur: file.productSpecs.alwaysColor
                ? file.pagesColour + file.pagesBW
                : file.pagesColour,
              aantalPaginasZwartWit: file.productSpecs.alwaysColor
                ? 0
                : file.pagesBW,
              aantalPaginasBlanco: file.pagesBlank,
              formaat: '',
              achterzijde: {},
              bindwijze: '',
              afwerking: [],
              dubbelzijdig: false,
              orderAfwerking: [],
              papierType: '',
              printKleur: '',
              voorzijde: {}
            }
          }

          let noPrice = false

          // Helper function to apply spec to a target object (for mailing: letterFile or attachmentFile)
          const applySpecToTarget = (spec, targetObj) => {
            const key = spec.priceKey
            const value = spec.priceValue || spec.optionName

            if (key === 'formaat') {
              targetObj.formaat = value
            } else if (spec.priceObjectKey === 'voorzijde') {
              if (key === 'printwijze') {
                targetObj.voorzijde.dubbelzijdig = value === 'Dubbelzijdig'
              } else if (key === 'printkleur') {
                targetObj.voorzijde.printKleur = value
              } else {
                targetObj.voorzijde[spec.optionGroupName] = value
              }
            } else if (spec.priceObjectKey === 'achterzijde') {
              if (key === 'printwijze') {
                targetObj.achterzijde.dubbelzijdig = value === 'Dubbelzijdig'
              } else if (key === 'printkleur') {
                targetObj.achterzijde.printKleur = value
              } else {
                targetObj.achterzijde[spec.optionGroupName] = value
              }
            } else if (key === 'afwerking') {
              targetObj.afwerking.push(value)
            } else if (key === 'orderafwerking') {
              targetObj.orderAfwerking.push(value)
            } else if (key === 'bindwijze') {
              targetObj.bindwijze = value
            } else if (key === 'printwijze') {
              targetObj.dubbelzijdig = value === 'Dubbelzijdig'
            } else if (key === 'printkleur') {
              targetObj.printKleur = value
            } else if (key === 'papierType') {
              targetObj.papierType = value
            }
          }

          // First pass: collect disabled priceObjectKeys from toggleOptions
          const disabledPriceObjectKeys = new Set()
          file.productSpecs.specs.forEach((spec) => {
            if (
              spec.optionGroupName === 'toggleOptions' &&
              spec.optionName === 'disabled' &&
              spec.priceObjectKey
            ) {
              disabledPriceObjectKeys.add(spec.priceObjectKey)
            }
          })

          file.productSpecs.specs.forEach((spec) => {
            if (spec.optionName === 'NONE') {
              return
            }

            if (spec.optionName === 'ALS_BESTAND') {
              noPrice = true
              return
            }

            if (
              spec.optionGroupName === 'toggleOptions' ||
              spec.optionGroupName === 'opmerking'
            ) {
              return
            }

            // mailing price calculation temp turned off
            if (file.productSpecs.priceType === 'mailing') {
              noPrice = true
              return
            }

            // Skip specs that belong to disabled menu groups (based on priceObjectKey)
            if (
              spec.priceObjectKey &&
              disabledPriceObjectKeys.has(spec.priceObjectKey)
            ) {
              return
            }

            // Handle mailing-specific options
            if (file.productSpecs.priceType === 'mailing') {
              if (spec.optionGroupName === 'mergeAddresses') {
                priceObject.mergeAddresses =
                  spec.optionName === 'true' || spec.optionName === true
                return
              } else if (spec.optionGroupName === 'envelopeEnclosing') {
                priceObject.envelopeEnclosing =
                  spec.optionName === 'true' || spec.optionName === true
                return
              }

              // Check if this spec is for attachment file or letter file
              if (spec.fileTypeRequired === 'ATTACHMENT_FILE') {
                // Apply to attachmentFile if it exists
                if (priceObject.attachmentFile) {
                  applySpecToTarget(spec, priceObject.attachmentFile)
                }
              } else {
                // Apply to letterFile (default when no fileTypeRequired or fileTypeRequired is not ATTACHMENT_FILE)
                applySpecToTarget(spec, priceObject.letterFile)
              }
            } else {
              // Non-mailing: apply specs directly to priceObject
              applySpecToTarget(spec, priceObject)
            }
          })
          if (options.spec.optionGroupName === 'opmerking') {
            return
          }

          // Cleanup empty orderAfwerking arrays
          if (file.productSpecs.priceType === 'mailing') {
            if (priceObject.letterFile.orderAfwerking.length === 0) {
              delete priceObject.letterFile.orderAfwerking
            }
            if (priceObject.attachmentFile?.orderAfwerking.length === 0) {
              delete priceObject.attachmentFile.orderAfwerking
            }
          } else {
            if (priceObject.orderAfwerking.length === 0) {
              delete priceObject.orderAfwerking
            }
          }

          const rootStore = useRootStore()

          // Add portal to correct location based on price type
          if (file.productSpecs.priceType === 'mailing') {
            // For mailing: add portal to letterFile and attachmentFile
            priceObject.letterFile.portal = rootStore.theme.themeName
            if (priceObject.attachmentFile) {
              priceObject.attachmentFile.portal = rootStore.theme.themeName
            }
          }
          // For non-mailing: add portal to root
          priceObject.portal = rootStore.theme.themeName

          //set quantity for price calculation (only for non-mailing orders)
          if (file.productSpecs.priceType !== 'mailing') {
            priceObject.aantalItems = file.quantity
          }

          if (noPrice) {
            this.updateProductOrderPrice([
              file.fileName,
              { pricePerItem: 'NA' }
            ])
            return
          }

          // Use correct API based on price type
          const priceCalculator =
            file.productSpecs.priceType === 'mailing'
              ? calculateMailingPrice
              : calculatePrice

          priceCalculator(priceObject)
            .then((response) => {
              // Only update price if this is still the latest request for this file
              if (
                this.priceRequestCounters[file.fileName] === currentRequestId
              ) {
                // Normalize mailing response to match standard response format
                let normalizedResponse = response
                if (
                  file.productSpecs.priceType === 'mailing' &&
                  typeof response === 'number'
                ) {
                  normalizedResponse = {
                    pricePerItem: response
                  }
                }
                this.updateProductOrderPrice([
                  file.fileName,
                  normalizedResponse
                ])
              }
            })
            .catch((error) => {
              if (error.status === 400) {
                return
              }
              console.log(error)
            })
        }
      })
    },
    async productOrderSpecUpdate(options) {
      this.fileList.forEach((file) => {
        if (options.fileNames.indexOf(file.fileName) > -1) {
          let specsArray

          if (options.hydrate) {
            specsArray = options.spec
          } else {
            specsArray = [...file.productSpecs.specs]

            specsArray.forEach((spec, index) => {
              if (
                options.spec.title === spec.title &&
                options.spec.optionGroupName === spec.optionGroupName
              ) {
                specsArray.splice(index, 1, { ...options.spec })
              }
            })
          }

          this.setProductOrderSpec([options.fileNames, specsArray])

          const rootStore = useRootStore()
          if (rootStore.theme.customFields.productOrder?.calculatePrice) {
            this.setProductOrderPrice(options)
          }
        }
      })
    }
  }
})
